import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { EVENT } from "../mock";

const H = ({ children }) => (
  <h2 className="font-heading uppercase text-white text-xl sm:text-2xl tracking-tight mt-12 mb-4">{children}</h2>
);
const P = ({ children }) => <p className="text-[#aeb9d4] text-sm leading-relaxed mb-4">{children}</p>;
const UL = ({ items }) => (
  <ul className="list-disc pl-6 text-[#aeb9d4] text-sm leading-relaxed mb-4 space-y-1.5">
    {items.map((it) => <li key={it}>{it}</li>)}
  </ul>
);

const LegalShell = ({ title, children, testId }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="bg-[#070b16] min-h-screen" data-testid={testId}>
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 pt-36 pb-24">
        <h1 className="font-heading uppercase text-white text-4xl sm:text-5xl tracking-tight mb-10" data-testid={`${testId}-headline`}>{title}</h1>
        {children}
        <H>Contact Us</H>
        <P>
          {EVENT.company}
          <br />{EVENT.address}
          <br />Attn: Oscar Tobar, President
          <br />Phone: <a href="tel:+18182310061" className="text-[#9db8e8] underline">{EVENT.phone}</a>
          <br />Email: <a href={`mailto:${EVENT.email}`} className="text-[#9db8e8] underline">{EVENT.email}</a>
          <br />Website: webinar.greatwesternfunding.com
        </P>
      </div>
      <Footer />
    </div>
  );
};

export const PrivacyPolicy = () => (
  <LegalShell title="Privacy Policy" testId="privacy-page">
    <P>
      Great Western Funding ("Great Western Funding," "we," "us," or "our") respects your privacy. This Privacy Policy
      explains how we collect, use, protect, and share information when you visit webinar.greatwesternfunding.com,
      submit a form, communicate with us, schedule an appointment, or use our services.
    </P>
    <H>Information We Collect</H>
    <P>We may collect information that you voluntarily provide to us, including:</P>
    <UL items={[
      "Name",
      "Email address",
      "Mobile or telephone number",
      "Mailing address",
      "Information submitted through website forms, appointment requests, webinar registrations, or inquiries",
      "Mortgage, real-estate, homebuying, refinance, or service-related information you choose to provide",
      "Communications and correspondence with us",
    ]} />
    <P>
      We may also automatically collect limited technical information when you use our website, such as your IP address,
      browser type, device information, pages viewed, and general usage data through cookies or similar technologies.
    </P>
    <H>How We Use Your Information</H>
    <P>We may use information we collect to:</P>
    <UL items={[
      "Respond to your requests, questions, and inquiries",
      "Schedule, confirm, and remind you about appointments or consultations",
      "Provide requested information, resources, customer service, and support",
      "Follow up on form submissions, webinar registrations, mortgage-related inquiries, or service requests",
      "Send educational content, homebuying or mortgage-related information, and promotional communications when permitted by law and your preferences",
      "Improve our website, communications, services, and user experience",
      "Protect our business, users, and website from fraud, misuse, or security issues",
      "Comply with applicable legal and regulatory obligations",
    ]} />
    <H>Text Messages and Mobile Information</H>
    <P>
      If you provide your mobile number and expressly opt in to receive text messages, Great Western Funding may send
      you SMS/MMS messages related to your inquiry, appointments, customer service, mortgage or real-estate information,
      educational content (including VA Purchasing Power webinar events), and promotional offers.
    </P>
    <P>
      <em>SMS consent is not a condition of purchase. Message frequency varies. Message and data rates may apply.</em>{" "}
      You may opt out at any time by replying STOP. For help, reply HELP or contact us at (818) 231-0061 or
      oscar@greatwesternfunding.com.
    </P>
    <P>
      Mobile information, including SMS opt-in data and consent, will not be shared with third parties or affiliates for
      their marketing or promotional purposes. Text messaging originator opt-in data and consent will not be shared with
      any third parties, excluding aggregators and providers of the text message services necessary to deliver our
      messages or as required by law. These providers are not permitted to use your mobile information for their own
      marketing purposes.
    </P>
    <H>SMS Opt-In and Opt-Out</H>
    <P>
      Consent to receive SMS messages is obtained exclusively through our website forms with a clear, unchecked opt-in
      checkbox. SMS consent is not a condition of purchasing any property, product, or service, and consent obtained for
      SMS messaging is not transferred or sold to any third party.
    </P>
    <UL items={[
      "Opt-out: Reply STOP at any time to unsubscribe. You will receive a final confirmation message and no further texts will be sent.",
      "Help: Reply HELP for assistance, or contact us at (818) 231-0061 or oscar@greatwesternfunding.com.",
      "Message frequency varies based on your inquiry and interactions.",
      "Message and data rates may apply depending on your mobile carrier plan.",
    ]} />
    <H>How We Share Information</H>
    <P>
      We may share information with trusted service providers that help us operate our website, manage communications,
      schedule appointments, deliver services, maintain records, or support our business operations. These providers may
      access information only as necessary to perform services for us and are expected to protect it.
    </P>
    <P>
      We may also disclose information when required by law; to respond to lawful requests; to protect the rights,
      safety, or property of Great Western Funding, our users, or others; or in connection with a business transaction
      such as a merger, acquisition, or sale of assets.
    </P>
    <P>We do not sell personal information for monetary consideration.</P>
    <H>Cookies and Similar Technologies</H>
    <P>
      We may use cookies and similar technologies to support website functionality, understand site usage, and improve
      the user experience. You can adjust your browser settings to refuse or delete cookies; however, some parts of our
      website may not function properly as a result.
    </P>
    <H>Data Security</H>
    <P>
      We use reasonable administrative, technical, and organizational safeguards designed to protect information in our
      possession. However, no method of transmission over the internet or electronic storage is completely secure, and
      we cannot guarantee absolute security.
    </P>
    <H>Your Choices</H>
    <P>
      You may request to update or correct certain personal information we hold about you. You may unsubscribe from
      marketing emails by using the unsubscribe link in the email, and you may opt out of text messages by replying
      STOP. To make a privacy request or ask questions about this Policy, contact us using the information below.
    </P>
    <H>Children's Privacy</H>
    <P>
      Our website and services are not directed to children under 13, and we do not knowingly collect personal
      information from children under 13. If you believe a child has provided us personal information, please contact us
      so we can take appropriate action.
    </P>
    <H>Third-Party Links</H>
    <P>
      Our website may contain links to third-party websites. We are not responsible for the content, privacy practices,
      or security of those websites. We encourage you to review their privacy policies before providing information.
    </P>
    <H>Changes to This Privacy Policy</H>
    <P>
      We may update this Privacy Policy from time to time. Any changes will be posted on this page with a revised
      effective date. Your continued use of our website or services after changes are posted indicates your acceptance
      of the updated policy.
    </P>
  </LegalShell>
);

export const TermsConditions = () => (
  <LegalShell title="Terms & Conditions" testId="terms-page">
    <P>
      Welcome to webinar.greatwesternfunding.com, operated by Great Western Funding ("we," "us," or "our"). By accessing
      or using this website, registering for a webinar, or submitting a form, you agree to these Terms & Conditions. If
      you do not agree, please do not use this website.
    </P>
    <H>Educational Purpose Only</H>
    <P>
      The VA Purchasing Power webinar and all related materials are provided for educational and informational purposes
      only. Nothing on this website constitutes a loan application, a commitment to lend, financial advice, or an offer
      of credit. Attending the webinar does not obligate you to apply for a loan or use our services, and registration
      does not require or initiate a hard credit inquiry.
    </P>
    <H>No Guarantee of Loan Approval</H>
    <P>
      Any examples, calculations, or discussions of purchasing power, entitlement, or loan terms are illustrative and
      general in nature. Actual eligibility, loan amounts, rates, and terms depend on individual circumstances and are
      subject to underwriting, program guidelines, and applicable law. Great Western Funding is not affiliated with, or
      endorsed by, the U.S. Department of Veterans Affairs or any government agency.
    </P>
    <H>Registration Information</H>
    <P>
      When registering for the webinar, you agree to provide accurate and current information. We may use the
      information you provide as described in our Privacy Policy, including to send you event confirmations, reminders,
      and follow-up materials.
    </P>
    <H>SMS Messaging Terms</H>
    <P>
      <strong className="text-white">Program Description:</strong> By opting in via our website registration form, you
      consent to receive SMS/MMS messages from Great Western Funding regarding your inquiry, appointment scheduling and
      reminders, webinar registrations and event updates, mortgage-related educational content, service updates,
      follow-up communications, and promotional offers.
    </P>
    <UL items={[
      "Consent: Consent is obtained through a clear, unchecked opt-in checkbox on our forms. Consent is not a condition of purchasing any property, product, or service.",
      "Message Frequency: Message frequency varies based on your inquiry and interactions with us.",
      "Fees: Message and data rates may apply depending on your mobile carrier and plan.",
      "Opt-Out: You can cancel the SMS service at any time by replying STOP. After you send STOP, we will confirm your unsubscribe status and you will no longer receive SMS messages from us. To rejoin, sign up again through our website form.",
      "Help: If you experience issues or need assistance, reply HELP, or contact us directly at (818) 231-0061 or oscar@greatwesternfunding.com.",
      "Carrier Liability: Carriers (e.g., AT&T, T-Mobile, Verizon) are not liable for delayed or undelivered messages.",
      "Privacy: For details on how we handle your information, including mobile opt-in data, see our Privacy Policy at webinar.greatwesternfunding.com/privacy-policy.",
    ]} />
    <P>
      Text messaging originator opt-in data and consent will not be shared with any third parties, excluding aggregators
      and providers of the text message services necessary to deliver our messages.
    </P>
    <H>Intellectual Property</H>
    <P>
      All content on this website and presented during the webinar — including text, graphics, logos, video, and
      materials — is the property of Great Western Funding or its licensors and is protected by applicable intellectual
      property laws. You may not reproduce, distribute, or create derivative works without our prior written consent.
    </P>
    <H>Acceptable Use</H>
    <P>
      You agree not to misuse this website, interfere with its operation, attempt unauthorized access, or use it for any
      unlawful purpose. We may suspend or terminate access for violations of these Terms.
    </P>
    <H>Disclaimer of Warranties</H>
    <P>
      This website and its content are provided "as is" and "as available" without warranties of any kind, express or
      implied. We do not warrant that the website will be uninterrupted, error-free, or free of harmful components.
    </P>
    <H>Limitation of Liability</H>
    <P>
      To the fullest extent permitted by law, Great Western Funding shall not be liable for any indirect, incidental,
      special, consequential, or punitive damages arising out of or related to your use of this website or attendance at
      the webinar.
    </P>
    <H>Third-Party Services</H>
    <P>
      This website may reference or link to third-party services. We are not responsible for the content or practices of
      third parties, and your use of third-party services is at your own risk and subject to their terms.
    </P>
    <H>Changes to These Terms</H>
    <P>
      We may update these Terms & Conditions from time to time. Changes will be posted on this page with a revised
      effective date. Continued use of the website after changes are posted constitutes acceptance of the updated Terms.
    </P>
    <H>Governing Law</H>
    <P>
      These Terms are governed by the laws of the State of California, without regard to conflict-of-law principles. Any
      disputes shall be resolved in the state or federal courts located in Los Angeles County, California.
    </P>
  </LegalShell>
);
